import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Heart, ShoppingBag, User, Menu, X, Sparkles, LayoutDashboard, ChevronDown, Phone, LogOut } from "lucide-react";
import { LOGO_URL } from "../../lib/constants";
import { useAuth } from "../../lib/auth";
import RateTicker from "./RateTicker";
import LanguageSwitcher from "./LanguageSwitcher";
import { api } from "../../lib/api";
import { assistGet, assistSubscribe } from "../../lib/assist";
import NotificationBell from "../NotificationBell";

// Default jewellery categories shown in the header mega menu. Categories added
// by admins from Products & Stock (Add Category) are merged in at runtime —
// see the "jewellery" branch below.
const BASE_NAV = [
  { id: "jewellery", tKey: "nav.jewellery", to: "/catalog", mega: [
    { name: "All Jewellery", to: "/catalog" },
  ]},
  { id: "weddings", tKey: "nav.weddings", to: "/catalog?occasion=Wedding" }, // mega filled at runtime
  { id: "occasions", tKey: "nav.occasions", to: "/catalog?occasion=Festival" }, // mega filled at runtime
  { id: "gold-programs", tKey: "nav.goldPrograms", to: "/gold-programs" },
  { id: "gemstones", tKey: "nav.gemstones", to: "/gemstones", mega: [
    { name: "All Gemstones", to: "/gemstones" },
    { name: "Diamond", to: "/gemstones?type=Diamond" },
    { name: "Precious Stone", to: "/gemstones?type=Precious%20Stone" },
    { name: "Semi-precious Stone", to: "/gemstones?type=Semi-precious%20Stone" },
  ] },
  { id: "bestseller", tKey: "nav.bestseller", to: "/catalog?is_bestseller=true", smallHide: true },
  { id: "gifting", tKey: "nav.gifting", to: "/catalog?occasion=Gifting", smallHide: true },
];

export default function Header() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState({ products: [], blogs: [] });
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMega, setActiveMega] = useState(null);
  const [openMobileSub, setOpenMobileSub] = useState(null);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [segments, setSegments] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [jewelleryCategories, setJewelleryCategories] = useState([]);
  // Grouped Jewellery menu: main category (sheet ItemName) -> designs (sheet Category)
  const [jewelleryMenu, setJewelleryMenu] = useState(null);
  const [openMobileFamily, setOpenMobileFamily] = useState(null);
  const inputRef = useRef(null);

  const isStaff = user && user.role !== "customer";
  // Assist mode: staff shopping on behalf of a walk-in customer
  const [assisting, setAssisting] = useState(assistGet());
  useEffect(() => assistSubscribe(() => setAssisting(assistGet())), []);

  useEffect(() => {
    api.get("/api/segments").then((r) => setSegments(r.data || [])).catch(() => {});
    api.get("/api/occasions").then((r) => setOccasions(r.data || [])).catch(() => {});
    api.get("/api/jewellery-menu").then((r) => setJewelleryMenu(r.data || { groups: [], extras: [] })).catch(() => {
      // Older backend without the grouped menu: fall back to the flat category list.
      api.get("/api/jewellery-categories").then((r) => setJewelleryCategories(r.data || [])).catch(() => {});
    });
  }, []);

  // Weddings mega: "All Wedding Jewellery" + each segment
  const weddingMega = [
    { name: "All Wedding Jewellery", to: "/catalog?occasion=Wedding" },
    ...segments.map((s) => ({ name: s.name, to: `/catalog?occasion=Wedding&segment=${encodeURIComponent(s.name)}` })),
  ];

  // Occasions mega: "All Occasions" overview + fixed "Festival" + each admin occasion
  const occasionsMega = [
    { name: "All Occasions", to: "/catalog?view=occasions" },
    { name: "Festival", to: "/catalog?occasion=Festival" },
    ...occasions.map((o) => ({ name: o.name, to: `/catalog?occasion=${encodeURIComponent(o.name)}` })),
  ];

  // Jewellery mega: "All Jewellery", then each MAIN category from the stock sheet
  // (BALI, BRACELET, CHAIN…) with its designs nested underneath (U BALI, V BALI…),
  // then any categories an admin added by hand in Products & Stock.
  const enc = encodeURIComponent;
  const jewelleryMega = jewelleryMenu
    ? [
        { name: "All Jewellery", to: "/catalog" },
        ...jewelleryMenu.groups.map((g) => (g.direct
          ? { name: g.name, to: `/catalog?design=${enc(g.children[0])}` }
          : { name: g.name, to: `/catalog?family=${enc(g.name)}`,
              children: g.children.map((c) => ({ name: c, to: `/catalog?design=${enc(c)}` })) })),
        ...jewelleryMenu.extras.map((n) => ({ name: n, to: `/catalog?category=${enc(n)}` })),
      ]
    : [
        { name: "All Jewellery", to: "/catalog" },
        ...jewelleryCategories.map((c) => ({ name: c.name, to: `/catalog?category=${enc(c.name)}` })),
      ];

  const NAV = BASE_NAV.map((item) => {
    if (item.id === "jewellery") return { ...item, mega: jewelleryMega };
    if (item.id === "weddings") return { ...item, mega: weddingMega };
    if (item.id === "occasions") return { ...item, mega: occasionsMega };
    return item;
  });

  useEffect(() => {
    if (!user) return;
    api.get("/api/wishlist").then((r) => setWishlistCount(r.data.items?.length || 0)).catch(() => {});
    api.get("/api/cart").then((r) => setCartCount(r.data.items?.length || 0)).catch(() => {});
  }, [user, assisting]);

  useEffect(() => {
    if (!searchQ) { setResults({ products: [], blogs: [] }); return; }
    const tmr = setTimeout(() => {
      api.get(`/api/search?q=${encodeURIComponent(searchQ)}`).then((r) => setResults(r.data)).catch(() => {});
    }, 220);
    return () => clearTimeout(tmr);
  }, [searchQ]);

  useEffect(() => {
    if (searchOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [searchOpen]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (searchQ.trim()) {
      setSearchOpen(false);
      navigate(`/catalog?q=${encodeURIComponent(searchQ)}`);
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-pearl/85 backdrop-blur-xl border-b border-line">
      <RateTicker />
      <div className="px-3 sm:px-5 lg:px-10 py-3 flex items-center gap-2 sm:gap-4 lg:gap-6 min-w-0">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <img src={LOGO_URL} alt="Swayambhu Gold" className="h-8 sm:h-9 lg:h-10 w-auto" />
        </Link>
        <nav className="hidden xl:flex items-center gap-3 2xl:gap-5 ml-2 flex-1 min-w-0">
          {NAV.map((item) => (
            <div
              key={item.id}
              className={`relative ${item.smallHide ? "hidden 2xl:block" : ""}`}
              onMouseEnter={() => item.mega && item.mega.length > 0 && setActiveMega(item.id)}
              onMouseLeave={() => setActiveMega(null)}
            >
              <Link
                to={item.to}
                data-testid={`nav-${item.id}`}
                className="text-[11px] 2xl:text-[12px] uppercase tracking-[0.1em] 2xl:tracking-[0.16em] font-semibold text-ink hover:text-maroon transition-colors py-2 inline-flex items-center whitespace-nowrap"
              >
                {t(item.tKey)}
              </Link>
              {item.mega && item.mega.length > 0 && activeMega === item.id && (
                <div className="absolute left-0 top-full pt-2 z-30">
                  {item.mega.some((m) => m.children) ? (
                    // Grouped panel: each main category is a heading with its designs beneath.
                    <div className="bg-white border border-line shadow-xl py-4 px-6 royal-frame w-[min(860px,92vw)] max-h-[70vh] overflow-y-auto" data-testid="mega-grouped">
                      <Link to={item.mega[0].to} onClick={() => setActiveMega(null)}
                        className="inline-block text-sm font-semibold text-maroon mb-3 hover:underline"
                        data-testid={`mega-${item.mega[0].name.toLowerCase().replace(/\s+/g, "-")}`}>
                        {item.mega[0].name} →
                      </Link>
                      <div className="columns-2 lg:columns-3 gap-8">
                        {item.mega.slice(1).map((m) => (
                          <div key={m.name} className="break-inside-avoid mb-4">
                            <Link to={m.to} onClick={() => setActiveMega(null)}
                              data-testid={`mega-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                              className="block text-[13px] font-semibold uppercase tracking-wide text-ink hover:text-maroon">
                              {m.name}
                            </Link>
                            {m.children && (
                              <div className="mt-1 space-y-0.5 border-l border-gold/40 pl-3">
                                {m.children.map((c) => (
                                  <Link key={c.name} to={c.to} onClick={() => setActiveMega(null)}
                                    data-testid={`mega-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="block text-[13px] text-ash hover:text-maroon">
                                    {c.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                  <div className="bg-white border border-line shadow-xl py-3 min-w-[240px] royal-frame">
                    {item.mega.map((m) => (
                      <Link
                        key={m.name}
                        to={m.to}
                        data-testid={`mega-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                        className="block px-5 py-2 text-sm text-ink hover:bg-pearl hover:text-maroon transition-colors"
                        onClick={() => setActiveMega(null)}
                      >
                        {m.name}
                      </Link>
                    ))}
                  </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {/* <Link
            to="/budget-match"
            data-testid="nav-budget-match"
            className="text-[12px] uppercase tracking-[0.18em] font-semibold text-gold-dark hover:text-maroon transition-colors py-2 inline-flex items-center gap-1"
          >
            ✦ Smart Budget Match
          </Link> */}
        </nav>
        <div className="flex items-center gap-0.5 sm:gap-1.5 lg:gap-3 ml-auto shrink-0">
          <button
            data-testid="header-search-toggle"
            onClick={() => setSearchOpen(true)}
            className="p-2 hover:text-maroon transition-colors"
            aria-label={t("nav.search")}
          >
            <Search size={18} />
          </button>

          {/* Staff: quick link back to their console — desktop only (tablet uses hamburger menu) */}
          {isStaff && (
            <Link
              to="/staff"
              data-testid="header-dashboard"
              className="hidden xl:inline-flex items-center gap-1 eyebrow text-maroon hover:text-gold transition-colors text-[10px]"
            >
             Dashboard
            </Link>
          )}
          {user && user.role === "customer" && (
            <Link
              to="/dashboard"
              data-testid="header-user-dashboard"
              className="hidden xl:inline-flex items-center gap-1 eyebrow text-maroon hover:text-gold transition-colors text-[10px] whitespace-nowrap"
            >
             My Dashboard
            </Link>
          )}

          <Link
            to="/exchange"
            data-testid="header-exchange"
            className="hidden xl:inline-flex eyebrow text-maroon hover:text-gold transition-colors text-[10px] whitespace-nowrap"
          >
            <Sparkles size={14} className="mr-1" /> {t("nav.exchange")}
          </Link>
          <a
            href="tel:+919980187987"
            data-testid="header-contact"
            className="hidden sm:inline-flex p-2 text-maroon hover:text-gold transition-colors"
            aria-label="Call Swayambhu Gold"
            title="Call +91 99801 87987"
          >
            <Phone size={18} />
          </a>
          {/* <div className="hidden md:block"><LanguageSwitcher compact /></div> */}

          {/* Cart & wishlist: customers always, and staff too — they build
              carts on behalf of walk-in customers. */}
          {(
            <>
              <Link to="/wishlist" data-testid="header-wishlist" className="relative p-2 hover:text-maroon" aria-label={t("nav.wishlist")}>
                <Heart size={18} />
                {wishlistCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-maroon text-pearl text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{wishlistCount}</span>
                )}
              </Link>
              <Link to="/cart" data-testid="header-cart" className="relative p-2 hover:text-maroon" aria-label={t("nav.cart")}>
                <ShoppingBag size={18} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-maroon text-pearl text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{cartCount}</span>
                )}
              </Link>
            </>
          )}

          {user && <NotificationBell />}

          {user ? (
            <div className="hidden md:flex items-center gap-1">
              <span className="text-xs whitespace-nowrap max-w-[7rem] truncate">Hi, {user.name?.split(" ")[0]}</span>
              <button
                onClick={logout}
                data-testid="header-logout"
                className="p-2 text-maroon hover:text-gold transition-colors"
                aria-label={t("nav.logout")}
                title={t("nav.logout")}
              >
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="header-login" className="p-2 hover:text-maroon" aria-label={t("nav.login")}><User size={18} /></Link>
          )}
          {user && (
            <Link
              to={isStaff ? "/staff" : "/dashboard"}
              className="md:hidden p-2 text-maroon hover:text-gold"
              aria-label={isStaff ? "Dashboard" : "My Dashboard"}
              title={isStaff ? "Dashboard" : "My Dashboard"}
              data-testid="header-dashboard-mobile"
            >
              <LayoutDashboard size={18} />
            </Link>
          )}
          <button className="xl:hidden p-2" onClick={() => { setMenuOpen(!menuOpen); setOpenMobileSub(null); }} data-testid="header-mobile-menu">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="xl:hidden bg-white border-t border-line px-5 py-4 space-y-1 max-h-[75vh] overflow-y-auto">
          {NAV.map((item) => {
            const hasSub = item.mega && item.mega.length > 0;
            const subOpen = openMobileSub === item.id;
            return (
              <div key={item.id} className="border-b border-line/60 last:border-b-0">
                <div className="flex items-center justify-between">
                  <Link
                    to={item.to}
                    onClick={() => { setMenuOpen(false); setOpenMobileSub(null); }}
                    className="flex-1 block py-3 text-sm font-medium text-ink hover:text-maroon"
                    data-testid={`mobile-nav-${item.id}`}
                  >
                    {t(item.tKey)}
                  </Link>
                  {hasSub && (
                    <button
                      type="button"
                      onClick={() => setOpenMobileSub(subOpen ? null : item.id)}
                      aria-label={`${subOpen ? "Collapse" : "Expand"} ${t(item.tKey)} submenu`}
                      aria-expanded={subOpen}
                      data-testid={`mobile-nav-toggle-${item.id}`}
                      className="p-3 -mr-2 text-ash hover:text-maroon"
                    >
                      <ChevronDown size={16} className={`transition-transform duration-200 ${subOpen ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
                {hasSub && (
                  <div className={`overflow-hidden transition-all duration-200 ${subOpen ? "max-h-[5000px] pb-2" : "max-h-0"}`}>
                    <div className="pl-3 border-l-2 border-gold/40 ml-1 space-y-0.5">
                      {item.mega.map((m) => {
                        const famKey = `${item.id}:${m.name}`;
                        const famOpen = openMobileFamily === famKey;
                        if (!m.children) {
                          return (
                            <Link
                              key={m.name}
                              to={m.to}
                              onClick={() => { setMenuOpen(false); setOpenMobileSub(null); }}
                              data-testid={`mobile-sub-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                              className="block py-2 pl-3 text-[13px] text-ash hover:text-maroon"
                            >
                              {m.name}
                            </Link>
                          );
                        }
                        // Main category: tap the name to see all of it, tap the arrow for its designs.
                        return (
                          <div key={m.name}>
                            <div className="flex items-center justify-between">
                              <Link
                                to={m.to}
                                onClick={() => { setMenuOpen(false); setOpenMobileSub(null); setOpenMobileFamily(null); }}
                                data-testid={`mobile-sub-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                                className="flex-1 block py-2 pl-3 text-[13px] font-semibold text-ink hover:text-maroon"
                              >
                                {m.name}
                              </Link>
                              <button type="button" onClick={() => setOpenMobileFamily(famOpen ? null : famKey)}
                                aria-expanded={famOpen} aria-label={`${famOpen ? "Collapse" : "Expand"} ${m.name}`}
                                data-testid={`mobile-family-toggle-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                                className="p-2 text-ash hover:text-maroon">
                                <ChevronDown size={14} className={`transition-transform duration-200 ${famOpen ? "rotate-180" : ""}`} />
                              </button>
                            </div>
                            {famOpen && (
                              <div className="ml-4 pl-3 border-l border-gold/30 space-y-0.5 mb-1">
                                {m.children.map((c) => (
                                  <Link key={c.name} to={c.to}
                                    onClick={() => { setMenuOpen(false); setOpenMobileSub(null); setOpenMobileFamily(null); }}
                                    data-testid={`mobile-sub-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="block py-1.5 text-[13px] text-ash hover:text-maroon">
                                    {c.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <Link to="/budget-match" onClick={() => setMenuOpen(false)} className="block py-3 text-sm text-gold-dark font-semibold hover:text-maroon" data-testid="mobile-nav-budget-match">✦ Smart Budget Match</Link>
          <Link to="/exchange" onClick={() => setMenuOpen(false)} className="block py-3 text-sm font-medium text-ink hover:text-maroon border-t border-line/60" data-testid="mobile-nav-exchange">{t("nav.exchange")}</Link>
          <Link to="/about" onClick={() => setMenuOpen(false)} className="block py-3 text-sm font-medium text-ink hover:text-maroon border-t border-line/60" data-testid="mobile-nav-about">{t("nav.about", "About")}</Link>
          <a href="tel:+919980187987" onClick={() => setMenuOpen(false)} className="block py-3 text-sm font-medium text-ink hover:text-maroon border-t border-line/60" data-testid="mobile-nav-contact">Call us · +91 99801 87987</a>
          {isStaff && (
            <Link
              to="/staff"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 py-2 text-sm font-semibold text-maroon border-t border-line mt-2 pt-3"
              data-testid="mobile-nav-dashboard"
            >
              <LayoutDashboard size={15} /> Go to Dashboard →
            </Link>
          )}
          {/* Customers had no way to reach their dashboard on phones or
              tablets — the desktop link was hidden below lg. */}
          {user && user.role === "customer" && (
            <Link
              to="/dashboard"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 py-2 text-sm font-semibold text-maroon border-t border-line mt-2 pt-3"
              data-testid="mobile-nav-user-dashboard"
            >
              <LayoutDashboard size={15} /> My Dashboard →
            </Link>
          )}
          {user && (
            <button
              onClick={() => { logout(); setMenuOpen(false); }}
              className="block w-full text-left py-2 text-sm text-maroon underline"
              data-testid="mobile-logout"
            >
              {t("nav.logout")}
            </button>
          )}
        </div>
      )}
      {searchOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 animate-fade-in" onClick={() => setSearchOpen(false)}>
          <div className="bg-pearl border-b border-line p-6 lg:p-10" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={onSubmit} className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 border-b-2 border-maroon pb-3">
                <Search size={22} className="text-maroon" />
                <input
                  ref={inputRef}
                  type="text"
                  data-testid="master-search-input"
                  placeholder="Search any item id, product name, category, HUID…"
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  className="flex-1 bg-transparent border-none outline-none text-lg font-display text-ink placeholder-ash/50"
                />
                <button type="button" onClick={() => setSearchOpen(false)} className="text-ash hover:text-maroon"><X size={20} /></button>
              </div>
              {searchQ && (
                <div className="mt-6 max-w-3xl mx-auto max-h-[60vh] overflow-y-auto">
                  {results.products?.length > 0 && (
                    <>
                      <div className="eyebrow mb-3">Products</div>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                        {results.products.slice(0, 8).map((p) => (
                          <Link key={p.id} to={`/product/${p.id}`} onClick={() => setSearchOpen(false)} className="block group" data-testid={`search-result-${p.id}`}>
                            <div className="zoom-frame bg-white border border-line aspect-square">
                              <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" />
                            </div>
                            <div className="text-xs mt-2 font-medium">{p.name}</div>
                            <div className="text-[10px] text-ash">{p.category}</div>
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                  {results.blogs?.length > 0 && (
                    <>
                      <div className="eyebrow mb-3">Blogs</div>
                      <div className="space-y-2">
                        {results.blogs.map((b) => (
                          <Link key={b.id} to={`/blog/${b.slug}`} onClick={() => setSearchOpen(false)} className="block py-2 border-b border-line hover:text-maroon" data-testid={`search-blog-${b.slug}`}>
                            <div className="text-sm font-medium">{b.title}</div>
                            <div className="text-xs text-ash">{b.excerpt}</div>
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                  {results.products?.length === 0 && results.blogs?.length === 0 && (
                    <div className="text-center text-ash py-12">No results for "{searchQ}"</div>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </header>
  );
}